import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, DollarSign } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const RevenueType = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // State
  const [revenueTypes, setRevenueTypes]             = useState([]);
  const [filteredRevenueTypes, setFilteredRevenueTypes] = useState([]);
  const [acHeads, setAcHeads]                       = useState([]);
  const [loading, setLoading]                       = useState(true);
  const [searchTerm, setSearchTerm]                 = useState('');
  const [showModal, setShowModal]                   = useState(false);
  const [editMode, setEditMode]                     = useState(false);
  const [submitLoading, setSubmitLoading]           = useState(false);

  const [formData, setFormData] = useState({
    RevenueTypeID: null,
    RevenueType:   '',
    RevenueCode:   '',
    AcHeadID:      null,
    RevenueCost:   0,
    MinimumValue:  0
  });

  const [errors, setErrors] = useState({});

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchRevenueTypes();
    fetchAcHeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterRevenueTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, revenueTypes]);

  // ── API calls ──────────────────────────────────────────────────────────────
  const fetchRevenueTypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/RevenueType.php');
      if (response.data.success) {
        setRevenueTypes(response.data.data || []);
      } else {
        alert('Failed to fetch revenue types: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching revenue types:', error);
      if (error.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching revenue types.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAcHeads = async () => {
    try {
      const response = await apiClient.get(`/RevenueType.php?action=acheads&companyid=${companyId}`);
      if (response.data.success) {
        setAcHeads(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching account heads:', error);
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filterRevenueTypes = () => {
    if (!searchTerm.trim()) {
      setFilteredRevenueTypes(revenueTypes);
    } else {
      const q = searchTerm.toLowerCase();
      setFilteredRevenueTypes(
        revenueTypes.filter(rt =>
          rt.RevenueType.toLowerCase().includes(q) ||
          rt.RevenueCode?.toLowerCase().includes(q)
        )
      );
    }
  };

  // Group acHeads by acclass for <optgroup>
  const groupedAcHeads = acHeads.reduce((acc, head) => {
    const cls = head.acclass || 'Other';
    if (!acc[cls]) acc[cls] = [];
    acc[cls].push(head);
    return acc;
  }, {});

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.RevenueType.trim())  newErrors.RevenueType  = 'Revenue type is required';
    if (!formData.RevenueCode?.trim()) newErrors.RevenueCode  = 'Revenue code is required';
    if (formData.RevenueCost  < 0)     newErrors.RevenueCost  = 'Revenue cost cannot be negative';
    if (formData.MinimumValue < 0)     newErrors.MinimumValue = 'Minimum value cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        RevenueCost:  parseFloat(formData.RevenueCost)  || 0,
        MinimumValue: parseFloat(formData.MinimumValue) || 0,
        AcHeadID:     formData.AcHeadID ? parseInt(formData.AcHeadID) : null
      };

      const method   = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/RevenueType.php', payload);

      if (response.data.success) {
        alert(editMode ? 'Revenue type updated successfully!' : 'Revenue type created successfully!');
        setShowModal(false);
        resetForm();
        fetchRevenueTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving revenue type:', error);
      if (error.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving revenue type: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Edit / Delete ──────────────────────────────────────────────────────────
  const handleEdit = (rt) => {
    setFormData({
      RevenueTypeID: rt.RevenueTypeID,
      RevenueType:   rt.RevenueType,
      RevenueCode:   rt.RevenueCode,
      AcHeadID:      rt.AcHeadID || null,
      RevenueCost:   parseFloat(rt.RevenueCost)  || 0,
      MinimumValue:  parseFloat(rt.MinimumValue) || 0
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this revenue type?')) return;
    try {
      const response = await apiClient.delete('/RevenueType.php', { data: { RevenueTypeID: id } });
      if (response.data.success) {
        alert('Revenue type deleted successfully!');
        fetchRevenueTypes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting revenue type:', error);
      alert('Error deleting revenue type: ' + (error.response?.data?.message || 'Please try again.'));
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ RevenueTypeID: null, RevenueType: '', RevenueCode: '', AcHeadID: null, RevenueCost: 0, MinimumValue: 0 });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const fmt = (v) => parseFloat(v || 0).toFixed(2);

  const profit        = parseFloat(formData.MinimumValue || 0) - parseFloat(formData.RevenueCost || 0);
  const profitPct     = formData.RevenueCost > 0
    ? ((profit / parseFloat(formData.RevenueCost)) * 100).toFixed(1)
    : 0;

  // ── No session ─────────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Revenue Type Master</h2>
          <p className="subtitle">Manage revenue types, costs, and default values</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} /> Add Revenue Type
        </button>
      </div>

      {/* Toolbar */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by revenue type or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredRevenueTypes.length}{' '}
          {filteredRevenueTypes.length === 1 ? 'revenue type' : 'revenue types'}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading revenue types...</div>
      ) : filteredRevenueTypes.length === 0 ? (
        <div className="empty-state">
          <p>No revenue types found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Revenue Type</th>
                <th>Account Head</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Min. Value</th>
                <th className="text-right">Profit</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRevenueTypes.map((rt) => {
                const cost    = parseFloat(rt.RevenueCost)  || 0;
                const minVal  = parseFloat(rt.MinimumValue) || 0;
                const pft     = minVal - cost;
                const pftPct  = cost > 0 ? ((pft / cost) * 100).toFixed(1) : 0;

                return (
                  <tr key={rt.RevenueTypeID}>
                    <td className="font-mono">{rt.RevenueCode}</td>
                    <td className="font-semibold">{rt.RevenueType}</td>
                    <td>
                      {rt.acheadname
                        ? <span title={rt.acheadcode}>{rt.acheadname}</span>
                        : <span style={{ color: '#9ca3af' }}>—</span>
                      }
                    </td>
                    <td className="text-right" style={{ fontFamily: 'monospace', color: '#dc2626' }}>
                      {fmt(rt.RevenueCost)}
                    </td>
                    <td className="text-right" style={{ fontFamily: 'monospace', color: '#16a34a' }}>
                      {fmt(rt.MinimumValue)}
                    </td>
                    <td className="text-right">
                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: pft >= 0 ? '#16a34a' : '#dc2626' }}>
                        {fmt(pft)}
                      </span>
                      <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '4px' }}>
                        ({pftPct}%)
                      </span>
                    </td>
                    <td className="actions-column">
                      <button className="btn-icon btn-edit"   onClick={() => handleEdit(rt)}                  title="Edit">   <Edit2  size={16} /></button>
                      <button className="btn-icon btn-delete" onClick={() => handleDelete(rt.RevenueTypeID)}  title="Delete"> <Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Revenue Type' : 'Add New Revenue Type'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4><DollarSign size={18} /> Revenue Information</h4>

                {/* Row 1: Code + Account Head */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="RevenueCode">
                      Revenue Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="RevenueCode"
                      value={formData.RevenueCode}
                      onChange={(e) => setFormData({ ...formData, RevenueCode: e.target.value.toUpperCase() })}
                      maxLength="20"
                      placeholder="e.g., FRT, DOC, CUS"
                      className={errors.RevenueCode ? 'error' : ''}
                    />
                    {errors.RevenueCode && <span className="error-message">{errors.RevenueCode}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="AcHeadID">Account Head</label>
                    <select
                      id="AcHeadID"
                      value={formData.AcHeadID || ''}
                      onChange={(e) => setFormData({ ...formData, AcHeadID: e.target.value ? parseInt(e.target.value) : null })}
                      style={{ width: '100%' }}
                    >
                      <option value="">— Select Account Head —</option>
                      {Object.entries(groupedAcHeads).map(([cls, heads]) => (
                        <optgroup key={cls} label={cls}>
                          {heads.map((h) => (
                            <option key={h.acheadid} value={h.acheadid}>
                              {h.acheadcode} — {h.acheadname}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Cost of Sales / Operating Income / Current Asset
                    </span>
                  </div>
                </div>

                {/* Revenue Type name */}
                <div className="form-group">
                  <label htmlFor="RevenueType">
                    Revenue Type <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="RevenueType"
                    value={formData.RevenueType}
                    onChange={(e) => setFormData({ ...formData, RevenueType: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Freight Charges, Documentation Fee"
                    className={errors.RevenueType ? 'error' : ''}
                    autoFocus
                  />
                  {errors.RevenueType && <span className="error-message">{errors.RevenueType}</span>}
                </div>

                {/* Row 2: Cost + Min Value */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="RevenueCost">Revenue Cost (Expense)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="RevenueCost"
                      value={formData.RevenueCost}
                      onChange={(e) => setFormData({ ...formData, RevenueCost: e.target.value })}
                      placeholder="0.00"
                      className={errors.RevenueCost ? 'error' : ''}
                      style={{ textAlign: 'right', fontFamily: 'monospace' }}
                    />
                    {errors.RevenueCost && <span className="error-message">{errors.RevenueCost}</span>}
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Your cost for this revenue type</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="MinimumValue">Minimum Value (Selling Price)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="MinimumValue"
                      value={formData.MinimumValue}
                      onChange={(e) => setFormData({ ...formData, MinimumValue: e.target.value })}
                      placeholder="0.00"
                      className={errors.MinimumValue ? 'error' : ''}
                      style={{ textAlign: 'right', fontFamily: 'monospace' }}
                    />
                    {errors.MinimumValue && <span className="error-message">{errors.MinimumValue}</span>}
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Default amount charged to customers</span>
                  </div>
                </div>

                {/* Live Profit Calculation */}
                {(parseFloat(formData.RevenueCost) > 0 || parseFloat(formData.MinimumValue) > 0) && (
                  <div style={{
                    marginTop: '20px', padding: '16px',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: `2px solid ${profit >= 0 ? '#22c55e' : '#f87171'}`,
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#16a34a', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase' }}>
                      Profit Calculation
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Cost</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626', fontFamily: 'monospace' }}>
                          {fmt(formData.RevenueCost)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Selling Price</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#16a34a', fontFamily: 'monospace' }}>
                          {fmt(formData.MinimumValue)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Profit</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: profit >= 0 ? '#16a34a' : '#dc2626', fontFamily: 'monospace' }}>
                          {fmt(profit)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>({profitPct}%)</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueType;